import { Empty, Icon, message, Spin } from 'antd';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { attachmentsActions } from '../../../actions/attachments.actions';
import { dspxSorter } from '../../../helpers/dspx-sort';
import usePagination from '../../../hooks/usePagination';
import Attachment from '../Attachment/Attachment';
import AttachmentButton from '../AttachmentButton/AttachmentButton';
import styles from './Attachments.module.scss';
import classNames from 'classnames';

const Attachments = ({
  className,
  textStyles,
  isAllowedDeleteThisAttachment,
  allowedDeleteAnyAttachment,
  hideUploadBox,
  postUrl,
  parentId,
  parentType,
  pageSize = 5,
  hideMetaData,
  needToBeUpdated = true,
  getAttachments,
  deleteAttachment,
  downloadAttachment,
  getPreviewAttachment,
  withBorder = true,
  acceptFormats = [],
}) => {
  const [attachments, setAttachments] = useState([]);
  const [currentPageNumber, setCurrentPageNumber, maxPage, shownItems] =
    usePagination(attachments, pageSize);
  const [loading, setLoading] = useState(true);
  const getAttachmentsList = useCallback(async (update) => {
    setLoading(true);
    try {
      let data;
      if (typeof getAttachments === 'function') {
        data = await getAttachments(parentId, update);
      } else {
        data = (await attachmentsActions.fetchAttachments(parentId, parentType))
          ?.data;
      }
      setAttachments([...data].sort(dspxSorter.sorterCreateDateDesc));
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
      message.error('Не получилось загрузить вложения');
    }
  }, [parentId, parentType, getAttachments]);

  useEffect(() => {
    if (needToBeUpdated) {
      getAttachmentsList();
    }
  }, [getAttachmentsList, needToBeUpdated]);

  useEffect(() => {
    if (!shownItems.length && currentPageNumber > 0) {
      setCurrentPageNumber(currentPageNumber - 1);
    }
  }, [shownItems, currentPageNumber]);

  const increasePage = useCallback(() => {
    setCurrentPageNumber((currentPageNumber) => {
      if (currentPageNumber < maxPage) {
        return currentPageNumber + 1;
      }
      return currentPageNumber;
    });
  }, [maxPage]);
  const decreasePage = useCallback(() => {
    setCurrentPageNumber((currentPageNumber) => {
      if (currentPageNumber > 0) {
        return currentPageNumber - 1;
      }
      return currentPageNumber;
    });
  }, []);
  return (
    <Spin spinning={loading}>
      <div className={classNames(styles.container, className, {
        [styles.border]: withBorder
      })}>
        <div className={styles.header}>
          <div className={styles.title}>Вложения</div>
          <div className={styles.pagination}>
            <Icon type="left" onClick={decreasePage} />
            {currentPageNumber + 1}/{maxPage || 1}
            <Icon type="right" onClick={increasePage} />
          </div>
        </div>
        {(!attachments || !attachments.length) && (
          <div className={styles.emptyContainer}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        )}
        <div className={styles.attachmentsList}>
          {shownItems?.map((attachment) => {
            let allowedDelete = allowedDeleteAnyAttachment;
            if (typeof isAllowedDeleteThisAttachment === 'function') {
              allowedDelete = isAllowedDeleteThisAttachment(attachment);
            }
            return (
              <Attachment
                getPreviewAttachment={getPreviewAttachment}
                customDeleteAttachment={deleteAttachment}
                customDownloadAttachment={downloadAttachment}
                allAttachments={attachments}
                hideMetaData={hideMetaData}
                shownItems={shownItems}
                parentId={parentId}
                parentType={parentType}
                getAttachmentsList={getAttachmentsList}
                attachment={attachment}
                key={attachment.id}
                allowedDelete={allowedDelete}
                textStyles={textStyles}
              />
            );
          })}
        </div>
        {!hideUploadBox && (
          <AttachmentButton
            postUrl={postUrl}
            getAttachmentsList={getAttachmentsList}
            acceptFormats={acceptFormats}
          />
        )}
      </div>
    </Spin>
  );
};

Attachments.propTypes = {
  isAllowedDeleteThisAttachment: PropTypes.func,
  allowedDeleteAnyAttachment: PropTypes.bool,
  hideUploadBox: PropTypes.bool,
  postUrl: PropTypes.string,
  parentId: PropTypes.number,
  parentType: PropTypes.oneOf(['documents', 'requests']),
  pageSize: PropTypes.number,
  hideMetaData: PropTypes.bool,
  needToBeUpdated: PropTypes.bool,
};
export default memo(Attachments);
